import { Typography } from '@/components'
import { Option, Select } from '@/components/atoms'
import { Breakpoint, ThemeColor } from '@/enums'
import { useSegment } from '@/modules'
import { useRecommendation, useUpdateRecommendationOptions } from '@/services'
import { PaymentPeriod } from '@/types/recommendation'
import { themeBreakpointUp } from '@/utils'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

const RecommendationPaymentPeriodSwitchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  ${themeBreakpointUp(Breakpoint.tab)} {
    padding-bottom: 16px;
  }
`

const SelectRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`

const SelectWrapper = styled.div`
  min-width: 200px;
`

const paymentPeriodOptions = [
  PaymentPeriod.YEARLY,
  PaymentPeriod.HALF_YEARLY,
  PaymentPeriod.QUARTERLY,
  PaymentPeriod.MONTHLY,
]

export const RecommendationPaymentPeriodSwitch = () => {
  const { t } = useTranslation()
  const analytics = useSegment()
  const { recommendationId } = useParams()
  const { data } = useRecommendation(recommendationId || '', {
    enabled: !!recommendationId,
  })
  const updateRecommendationOptionsMutation = useUpdateRecommendationOptions(
    recommendationId || '',
  )
  const selectedPaymentPeriod =
    data?.searchCriteria?.paymentPeriod ?? PaymentPeriod.YEARLY

  const handleChange = useCallback(
    async (paymentPeriod: PaymentPeriod) => {
      if (recommendationId) {
        updateRecommendationOptionsMutation.mutateAsync({
          recommendationId,
          paymentPeriod,
        })
        await analytics.track('recommendation_customisation_payment_period', {
          period: paymentPeriod,
        })
      }
    },
    [updateRecommendationOptionsMutation, recommendationId, analytics],
  )

  if (!recommendationId) return null

  return (
    <RecommendationPaymentPeriodSwitchWrapper>
      <SelectRow>
        <Typography variant="p1Body" noWordBreak={true}>
          {t('offerRecommendation.paymentPeriodLabel')}
        </Typography>
        <SelectWrapper>
          <Select
            id="payment-period-select"
            onSelect={(value?: string | number) =>
              handleChange(value as PaymentPeriod)
            }
            value={selectedPaymentPeriod}
          >
            {paymentPeriodOptions.map((period) => (
              <Option key={period} value={period}>
                {t(`offerRecommendation.paymentPeriodSelect.${period}`)}
              </Option>
            ))}
          </Select>
        </SelectWrapper>
      </SelectRow>

      {selectedPaymentPeriod !== PaymentPeriod.YEARLY ? (
        <Typography variant="p2Body" color={ThemeColor.b50}>
          {t('offerRecommendation.installmentSurchargeNotice')}
        </Typography>
      ) : null}
    </RecommendationPaymentPeriodSwitchWrapper>
  )
}
